import { useMemo, useState } from 'react';
import { Card, Button, Input, Tag, Space, Typography, Select, Progress, Tooltip, FilterResetButton, Popconfirm, message, theme, StatCard, ActionMenu, Modal, Dropdown, MenuItem, MenuLabel, MenuDivider } from '../../ui';
import type { TagTone } from '../../ui';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import {
  Plus, Search, Phone, MessageCircle,
  MapPin, Eye, Download, Upload, Pencil, Merge, ArrowUpDown, FileSpreadsheet, FileDown,
  Ban, CheckCircle2, Store, ShieldCheck, IndianRupee, FileWarning, FileStack,
} from 'lucide-react';
import { request, api } from '../../api/client';
import { requestAllPages } from '../../api/allPages';
import { usePermission, usePermissionChecker } from '../../store/auth';
import { useDebounce } from '../../hooks/useDebounce';
import { useUrlString, useUrlNumber, useUrlBool, useUrlList } from '../../hooks/useUrlState';
import { useServerSort } from '../../hooks/useServerSort';
import { DataTable, DataTableColumn } from '../../components/DataTable';
import { RetailerFormModal } from './RetailerFormModal';
import { BulkImportModal } from '../../components/BulkImportModal';
import { ExportDialog } from '../../components/ExportDialog';
import { RegisteredBy } from '../../components/RegisteredBy';
import { downloadCsv } from '../../lib/downloadCsv';
import { MergeCustomerModal } from '../territory/MergeCustomerModal';
import { TerritoryChips } from '../../components/TerritoryChips';
import { displayMobile, waNumber } from '../../lib/validators';
import { CopyValue } from '../../components/CopyValue';
import { formatIstDate } from '../../lib/time';
import { STATE_NAME } from '../../constants/indianStates';
import { facetOptions, renderFacetOption } from '../../lib/facetOptions';
import { inr2 } from '../../lib/format';
import { PALETTE, TAG_TONE_HUE, filterTint } from '../../lib/palette';
import { hueOption } from '../../components/FilterPanel';
import { retailerStatusLabel } from '../../lib/statusTone';
import { inr as inrWhole } from '../../lib/format';
import { territoryOption } from '../../lib/territoryLabel';

// The "KYC details" filter's colours. Red is what blocks KYC — the same red the missing-item tags wear;
// "No GSTIN" is amber like its "Not recorded" tag on the 360 page (worth collecting, never blocking);
// complete is green.
const KYC_FILTER_HUE: Record<string, string> = {
  pending: TAG_TONE_HUE.red,
  agriLicenseNo: TAG_TONE_HUE.red,
  agriLicenseCopy: TAG_TONE_HUE.red,
  pan: TAG_TONE_HUE.red,
  panNumber: TAG_TONE_HUE.red,
  gstNumber: TAG_TONE_HUE.gold,
  complete: TAG_TONE_HUE.green,
};

// Short forms for the register's one-line KYC chips; the tooltip carries the full names.
const KYC_SHORT: Record<string, string> = { 'Agri licence no.': 'Lic. no.', 'Agri licence copy': 'Lic. copy' };

const STATUS_COLOR: Record<string, TagTone> = {
  ACTIVE: 'green', KYC_PENDING: 'gold', PROSPECT: 'blue', SUSPENDED: 'red', CLOSED: 'neutral',
};
const STATE_LABEL = STATE_NAME;
// The shared formatter: a negative reads "-₹480", never "₹-480".
const inr = inrWhole;
const opt = facetOptions;

// Dealer-agreement status from its expiry date. "alert" rows are tinted red in the
// table so expiring/expired agreements stand out. Threshold: 30 days.
const AGREEMENT_WARN_DAYS = 30;
function agreementStatus(expiry?: string | null, copyUrl?: string | null): { key: string; label: string; color: TagTone; alert: boolean } {
  if (!expiry) return { key: 'none', label: 'No agreement', color: 'neutral', alert: false };
  // A date with no signed document is a claim, not an agreement — it used to render a green "Active".
  if (!copyUrl) return { key: 'nocopy', label: 'Copy missing', color: 'gold', alert: true };
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const exp = new Date(expiry); exp.setHours(0, 0, 0, 0);
  const days = Math.round((exp.getTime() - today.getTime()) / 86_400_000);
  if (days < 0) return { key: 'expired', label: `Expired ${-days}d ago`, color: 'red', alert: true };
  if (days <= AGREEMENT_WARN_DAYS) return { key: 'expiring', label: `Expires in ${days}d`, color: 'gold', alert: true };
  return { key: 'active', label: 'Active', color: 'green', alert: false };
}

export function RetailersListPage({ embedded }: { embedded?: boolean } = {}) {
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const [page, setPage] = useUrlNumber('page', 1);
  const [pageSize, setPageSize] = useUrlNumber('pageSize', 20);
  const [searchInput, setSearchInput] = useUrlString('q', '');
  const search = useDebounce(searchInput, 400);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const hasPerm = usePermissionChecker();
  const canCreate = hasPerm('RETAILER.CREATE');
  const canEdit = hasPerm('RETAILER.EDIT');
  const canExport = hasPerm('RETAILER.EXPORT');
  const canImport = hasPerm('RETAILER.IMPORT');
  const canMerge = hasPerm('TERRITORY.SPLIT_MERGE');
  const canStatus = hasPerm('RETAILER.STATUS'); // activate/deactivate — Admin / Super Admin only
  const [mergingDup, setMergingDup] = useState<any | null>(null);
  const qc = useQueryClient();
  const statusMut = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      request({ url: `/retailers/${id}/status`, method: 'PATCH', data: { active } }),
    onSuccess: (_d, v) => {
      message.success(v.active ? 'Retailer activated' : 'Retailer deactivated');
      qc.invalidateQueries({ queryKey: ['retailers'] });
    },
    onError: (e: any) => message.error(e?.response?.data?.error?.message ?? 'Failed to change status'),
  });

  // ---- Filters (server-side) ----
  const [statusF, setStatusF] = useUrlList('status');
  const [territoryF, setTerritoryF] = useUrlList('territory');
  const [salesF, setSalesF] = useUrlList('sales');
  const [stateF, setStateF] = useUrlList('state');
  const [cityF, setCityF] = useUrlList('city');
  const [creditF, setCreditF] = useUrlList('credit');
  const [hasOutstanding, setHasOutstanding] = useUrlBool('outstanding');
  // Dealer agreements already expired or inside the 30-day renewal window — the tile is the filter.
  const [agreementDue, setAgreementDue] = useUrlBool('agreementDue');
  // '' | 'pending' | 'complete' — which side of the document checklist to show.
  const [docsPending, setDocsPending] = useUrlString<string | undefined>('docs', undefined);
  // "KYC pending" means NOT VERIFIED — which is a different set from status='KYC_PENDING' (a PROSPECT
  // or SUSPENDED dealer is equally unverified). Filtering by status made the tile say 36 and the
  // table show 4; a tile must return exactly the rows it counted.
  const [kycPendingOnly, setKycPendingOnly] = useUrlBool('kycPending');
  // Server-side column sort (backend ORDER BY over the full result set, not just this page).
  const { sortState, onSortChange, sort } = useServerSort({ onChange: () => setPage(1) });

  const params = useMemo(() => {
    const p: Record<string, any> = { page, pageSize };
    if (search.trim()) p.search = search.trim();
    if (statusF.length) p.status = statusF.join(',');
    if (territoryF.length) p.territoryId = territoryF.join(',');
    if (salesF.length) p.assignedSalesPersonId = salesF.join(',');
    if (stateF.length) p.stateCode = stateF.join(',');
    if (cityF.length) p.city = cityF.join(',');
    if (creditF.length) p.creditPeriodDays = creditF.join(',');
    if (hasOutstanding) p.hasOutstanding = 'true';
    if (agreementDue) p.agreementDue = 'true';
    if (kycPendingOnly) p.kycPending = 'true';
    if (docsPending) p.docsPending = docsPending;
    if (sort) p.sort = sort;
    return p;
  }, [page, pageSize, search, statusF, territoryF, salesF, stateF, cityF, creditF, hasOutstanding, agreementDue, kycPendingOnly, docsPending, sort]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['retailers', params],
    queryFn: () => request<any>({ url: '/retailers', params }),
    placeholderData: keepPreviousData,
  });
  const { data: facets } = useQuery({
    queryKey: ['retailers', 'filter-options'],
    queryFn: () => request<any>({ url: '/retailers/filter-options' }),
  });
  const { data: territories } = useQuery({
    queryKey: ['territories-list'],
    queryFn: () => request<any[]>({ url: '/territory-assignments/me/territories' }),
  });
  // The owner filter reads the staff directory, which needs USER.VIEW — a Sales Person does not hold it
  // (and only ever gets their own retailers back anyway), so asking logged a 403 on every page load.
  const canViewUsers = usePermission('USER.VIEW');
  const { data: salesUsers } = useQuery({
    queryKey: ['sales-persons-list'],
    queryFn: () => requestAllPages('/users', { role: 'SALES_PERSON' }),
    enabled: canViewUsers,
  });

  const kpis: Record<string, number> = facets?.kpis ?? {};
  const kycOpt = (value: string, text: string, count?: number) =>
    ({ ...hueOption(value, text, KYC_FILTER_HUE[value]), count });
  const kycDetailOptions: any[] = [
    { label: 'Missing for KYC', options: [
      kycOpt('pending', 'Anything missing', kpis.docsPending),
      kycOpt('agriLicenseNo', 'Agri licence no. missing', kpis.missingAgriLicenseNo),
      kycOpt('agriLicenseCopy', 'Agri licence copy missing', kpis.missingAgriLicenseCopy),
      kycOpt('pan', 'PAN missing', kpis.missingPan),
    ] },
    // A list, not a KYC gap: GSTIN is optional (small unregistered dealers have none).
    { label: 'Optional', options: [
      kycOpt('gstNumber', 'No GSTIN recorded', kpis.noGstNumber),
    ] },
    { label: 'Complete', options: [
      kycOpt('complete', 'Nothing missing', kpis.total != null && kpis.docsPending != null ? kpis.total - kpis.docsPending : undefined),
    ] },
  ];
  const noFilters = !statusF.length && !territoryF.length && !salesF.length && !stateF.length
    && !cityF.length && !creditF.length && !hasOutstanding && !agreementDue && !kycPendingOnly && !docsPending && !search;

  const stateOpts = useMemo(() => facetOptions(facets?.stateCode, (s) => String(STATE_LABEL.get(String(s.value)) ?? s.value)), [facets]);
  const creditOpts = useMemo(() => facetOptions(facets?.creditPeriodDays, (c) => `${c.value} days`), [facets]);

  const salesPersonMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const u of (salesUsers?.data ?? [])) m.set(String(u.id), u.fullName ?? u.name ?? u.mobile);
    return m;
  }, [salesUsers]);

  const activeFilters = statusF.length + territoryF.length + salesF.length + stateF.length + cityF.length + creditF.length + (hasOutstanding ? 1 : 0) + (agreementDue ? 1 : 0) + (kycPendingOnly ? 1 : 0) + (docsPending ? 1 : 0) + (search ? 1 : 0);
  const clearFilters = () => {
    setSearchInput(''); setStatusF([]); setTerritoryF([]); setSalesF([]); setStateF([]); setCityF([]); setCreditF([]); setHasOutstanding(false); setAgreementDue(false); setKycPendingOnly(false); setDocsPending(undefined); setPage(1);
  };

  // Activate/deactivate moved into the kebab, so its confirmation is a dialog keyed to the chosen
  // row — a Popconfirm anchored to a menu item is dismissed along with the menu.
  const [confirmStatus, setConfirmStatus] = useState<any | undefined>();

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get('/retailers/export.csv', { responseType: 'blob', params });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url; a.download = 'retailers.csv'; a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  const columns: DataTableColumn<any>[] = [
    { key: 'code', title: 'Code', dataIndex: 'code', width: 120, sortable: true, alwaysVisible: true,
      render: (v: string, r: any) => <Link to={`/retailers/${r.id}`} onClick={(e) => e.stopPropagation()}>{v}</Link> },
    {
      key: 'companyName', title: 'Company', dataIndex: 'companyName', width: 220, sortable: true, alwaysVisible: true,
      render: (v: string, r: any) => (
        <Tooltip label={`${v}${r.contactPerson ? ` · ${r.contactPerson}` : ''}`}>
          <div style={{ maxWidth: 200 }}>
            <div style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{v}</div>
            <div style={{ fontSize: 12, color: token.colorTextTertiary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.contactPerson}</div>
          </div>
        </Tooltip>
      ),
    },
    { key: 'mobile', title: 'Mobile', dataIndex: 'mobile', width: 140,
      /*
       * Click-to-copy rather than a `tel:` link. A number in a register exists to be pasted
       * somewhere else — WhatsApp, Tally, a support ticket — and it could not be picked up by
       * dragging across it, because a drag inside the table is the grab-and-pan gesture
       * (see ui/useDragScroll). One click now puts it on the clipboard; panning is untouched,
       * since a pan only starts after the pointer has travelled far enough not to be a click.
       * Calling is unaffected: it was already a row-kebab action.
       */
      render: (v: string) => v && <CopyValue value={v} label="Mobile">{displayMobile(v)}</CopyValue> },
    { key: 'location', title: 'Location', dataIndex: 'city', width: 200, sortable: true, ellipsis: true, alwaysVisible: true,
      render: (city: string, r: any) => {
        const state = r.stateCode ? (STATE_LABEL.get(r.stateCode) ?? r.stateCode) : null;
        const txt = [city, state].filter(Boolean).join(' · ');
        return txt || <span style={{ color: '#bbb' }}>—</span>;
      } },
    /*
     * WHERE the dealer sits, as the whole chain rather than its leaf.
     *
     * This printed `MH-PUNE · Pune` — a code the reader has to know and a name with no level on
     * it, in the same grey as every other cell. The chips give each level its own colour and glyph,
     * and they resolve against the FULL territory master: the map this used to read was
     * `/territory-assignments/me/territories`, the viewer's OWN book, so a dealer sitting outside it
     * rendered an em-dash as if it had no territory at all.
     */
    { key: 'territoryId', title: 'Territory', dataIndex: 'territoryId', width: 300,
      render: (v: any, r: any) => <TerritoryChips territoryId={v} fallbackName={r.territoryName} /> },
    {
      // KYC as its own column rather than folded into Status: a retailer's status has five values
      // that mean commercial things (PROSPECT, SUSPENDED, CLOSED…), and KYC is a separate axis with
      // its own trail. "Awaiting" is the one that needs somebody to act.
      // A file nobody has submitted yet shows WHAT it is waiting for instead of the word "Pending" —
      // it cannot be submitted until those items are in, so the list is the more useful status. One
      // that is complete says "Ready to submit". Verified / Awaiting / Sent back keep their chip, with
      // any missing items on hover: red chips on every grandfathered dealer would bury the ones that
      // actually need chasing. The "KYC details" filter lists them item by item.
      key: 'kyc', title: 'KYC', width: 190,
      render: (_: any, r: any) => {
        const missing: string[] = r.kycMissingDocuments ?? [];
        if (!r.kycVerifiedAt && !r.kycSubmittedAt && !r.kycRejectedReason) {
          // ONE line, short names — stacked full names tripled the row height. The full names are on hover.
          return missing.length
            ? (
              <Tooltip label={`Missing: ${missing.join(', ')}`}>
                <span className="flex flex-wrap gap-1">
                  {missing.map((m) => <Tag key={m} tone="red">{KYC_SHORT[m] ?? m}</Tag>)}
                </span>
              </Tooltip>
            )
            : <Tag tone="cyan">Ready to submit</Tag>;
        }
        const tag = r.kycVerifiedAt
          ? <Tag tone="green">Verified</Tag>
          : r.kycSubmittedAt
            ? <Tag tone="blue">Awaiting</Tag>
            : <Tag tone="red">Sent back</Tag>;
        const tip = [
          !r.kycVerifiedAt && !r.kycSubmittedAt ? `Sent back: ${r.kycRejectedReason}` : '',
          missing.length ? `Missing: ${missing.join(', ')}` : '',
        ].filter(Boolean).join(' · ');
        return tip ? <Tooltip label={tip}><span>{tag}</span></Tooltip> : tag;
      },
    },
    // Beside KYC because the GSTIN is the identity KYC hangs on. It deliberately KEEPS the key of the
    // "KYC missing" column it replaced: saved column layouts are keyed lists, and TwDataTable resets a
    // layout that names a key the table no longer has — so a new key would have wiped every user's
    // column choices, and promoting the old hidden `gstNumber` column would have reached nobody who
    // had ever saved a layout. Reusing the key shows the GSTIN exactly where "KYC missing" was.
    // A blank GSTIN says "Not recorded" in quiet grey — optional, so it must not compete with the red
    // KYC chips for attention, but a bare dash made the "No GSTIN recorded" filter's rows look unexplained.
    { key: 'docsPending', title: 'GSTIN', dataIndex: 'gstNumber', width: 180,
      render: (v: string) => (v
        ? <CopyValue value={v} label="GSTIN" truncate>{v}</CopyValue>
        : <Tooltip label="Optional — not required for KYC"><span className="type-caption text-subtle">Not recorded</span></Tooltip>) },
    { key: 'status', title: 'Status', dataIndex: 'status', width: 120, sortable: true,
      render: (s: string) => <Tag tone={STATUS_COLOR[s] ?? 'neutral'}>{retailerStatusLabel(s)}</Tag> },
    {
      key: 'creditUsage', title: 'Credit Usage', width: 200,
      sorter: (a: any, b: any) => {
        const ua = Number(a.creditLimit) > 0 ? Number(a.outstandingBalance) / Number(a.creditLimit) : 0;
        const ub = Number(b.creditLimit) > 0 ? Number(b.outstandingBalance) / Number(b.creditLimit) : 0;
        return ua - ub;
      },
      render: (_: any, r: any) => {
        const limit = Number(r.creditLimit) || 0;
        const out = Number(r.outstandingBalance) || 0;
        // No sanctioned limit → there is no usage to meter. An empty "₹0 / ₹0" bar read as "0% used".
        if (limit <= 0) {
          return out > 0
            ? <span style={{ fontSize: 12, color: PALETTE.danger[500] }}>No credit · {inr2(out)} due</span>
            : <span className="type-caption text-subtle">No credit</span>;
        }
        const pct = limit > 0 ? Math.min(100, Math.round((out / limit) * 100)) : 0;
        const strokeColor = pct >= 90 ? PALETTE.danger[500] : pct >= 60 ? PALETTE.harvest[400] : PALETTE.success[500];
        return (
          <Tooltip block label={`Outstanding ${inr2(out)} of ${inr2(limit)} · available ${inr2(limit - out)}`}>
            <div className="w-full">
              <Progress percent={pct} size="small" strokeColor={strokeColor} showInfo={false} />
              <div style={{ fontSize: 11, color: token.colorTextSecondary }}>{inr2(out)} / {inr2(limit)}</div>
            </div>
          </Tooltip>
        );
      },
    },
    { key: 'creditLimit', title: 'Credit Limit', dataIndex: 'creditLimit', width: 130, align: 'right', sortable: true, render: (v: any) => inr2(v) },
    { key: 'outstandingBalance', title: 'Outstanding', dataIndex: 'outstandingBalance', width: 140, align: 'right', sortable: true,
      render: (v: any) => <span style={{ color: Number(v) > 0 ? PALETTE.danger[500] : undefined }}>{inr2(v)}</span> },
    { key: 'creditPeriodDays', title: 'Credit Days', dataIndex: 'creditPeriodDays', width: 110, align: 'right', defaultVisible: false },
    /*
     * GSTIN / PAN / agri licence are click-to-copy for the same reason the mobile is: they are
     * transcribed into Tally, an e-way bill or a licence renewal, and a drag across them pans the
     * table instead of selecting (ui/useDragScroll). The detail page already copies these three
     * this way — the register now matches it.
     */
    { key: 'panNumber', title: 'PAN', dataIndex: 'panNumber', width: 130, ellipsis: true, defaultVisible: false,
      render: (v: string) => (v
        ? <CopyValue value={v} label="PAN" truncate>{v}</CopyValue>
        : <span style={{ color: token.colorTextTertiary }}>—</span>) },
    { key: 'assignedSalesPersonId', title: 'Assigned SP', dataIndex: 'assignedSalesPersonId', width: 170, ellipsis: true,
      render: (v: any) => (v && salesPersonMap.get(String(v))) || <span style={{ color: token.colorTextTertiary }}>—</span> },
    { key: 'agriLicenseNo', title: 'Agri License No.', dataIndex: 'agriLicenseNo', width: 180, ellipsis: true, alwaysVisible: true,
      render: (v: string) => (v
        ? <CopyValue value={v} label="Agri licence no." truncate>{v}</CopyValue>
        : <span style={{ color: token.colorTextTertiary }}>—</span>) },
    { key: 'pincode', title: 'Pincode', dataIndex: 'pincode', width: 100, defaultVisible: false },
    { key: 'email', title: 'Email', dataIndex: 'email', width: 200, ellipsis: true, defaultVisible: false,
      render: (v: string) => v && <a href={`mailto:${v}`} onClick={(e) => e.stopPropagation()}>{v}</a> },
    {
      // App access, same pair the 360 page shows: can they sign in, and is a handset already tied to
      // them? One-device-per-user means "bound to an old phone" is a real support case, and spotting
      // it from the register beats opening records one at a time.
      key: 'device', title: 'Device', width: 120,
      render: (_: any, r: any) => (r.deviceBound
        ? <Tooltip label={r.deviceSuffix ? `Bound to a handset ending …${r.deviceSuffix}` : 'A handset is bound'}><span><Tag tone="green">Bound</Tag></span></Tooltip>
        : <Tag>Not bound</Tag>) },
    { key: 'login', title: 'App login', width: 130, defaultVisible: false,
      render: (_: any, r: any) => (r.appUserId
        ? <Tag tone={r.loginStatus === 'active' ? 'green' : 'gold'}>{String(r.loginStatus ?? 'unknown')}</Tag>
        : <Tag>Never logged in</Tag>) },
    { key: 'onboardedAt', title: 'Onboarded', dataIndex: 'onboardedAt', width: 130, sortable: true, defaultVisible: false, render: (v: string) => formatIstDate(v) },
    {
      key: 'agreement', title: 'Agreement', width: 160, sortable: true,
      sorter: (a: any, b: any) => String(a.agreementExpiry ?? '').localeCompare(String(b.agreementExpiry ?? '')),
      render: (_: any, r: any) => {
        const s = agreementStatus(r.agreementExpiry, r.agreementUrl);
        return (
          <div>
            <Tag tone={s.color}>{s.label}</Tag>
            {r.agreementExpiry && <div style={{ fontSize: 11, color: token.colorTextTertiary }}>till {formatIstDate(r.agreementExpiry)}</div>}
          </div>
        );
      },
    },
    { key: 'createdAt', title: 'Created', dataIndex: 'createdAt', width: 130, sortable: true, alwaysVisible: true, render: (v: string) => formatIstDate(v) },
    // Who added the dealer (and in which shift) — the Created date beside it is WHEN, this is WHO.
    { key: 'registeredBy', title: 'Registered by', width: 190, render: (_: any, r: any) => <RegisteredBy r={r} compact /> },
    {
      /**
       * Two inline actions, the rest behind the kebab. The row carried seven 32px icon buttons —
       * open, edit, activate, merge, call, WhatsApp, map — which is more chrome than data and made
       * the column wide enough for all seven on every row, even though five are occasional.
       */
      key: 'actions', title: '', width: 140, alwaysVisible: true, fixed: 'right',
      render: (_: any, r: any) => {
        const items = [
          { key: 'call', label: 'Call', icon: <Phone size={16} />, onSelect: () => { window.location.href = `tel:${displayMobile(r.mobile)}`; } },
          { key: 'wa', label: 'WhatsApp', icon: <MessageCircle size={16} />, onSelect: () => window.open(`https://wa.me/${waNumber(r.whatsappNumber ?? r.mobile)}`, '_blank', 'noopener') },
          { key: 'map', label: 'View on map', icon: <MapPin size={16} />, onSelect: () => window.open(`https://www.google.com/maps/search/?api=1&query=${r.latitude},${r.longitude}`, '_blank', 'noopener') },
          canMerge && { key: 'merge', label: 'Merge duplicate', icon: <Merge size={16} />, dividerBefore: true, onSelect: () => setMergingDup(r) },
          canStatus && {
            key: 'status',
            label: r.status === 'ACTIVE' ? 'Deactivate retailer' : 'Activate retailer',
            icon: r.status === 'ACTIVE' ? <Ban size={16} /> : <CheckCircle2 size={16} />,
            danger: r.status === 'ACTIVE',
            dividerBefore: true,
            onSelect: () => setConfirmStatus(r),
          },
        ].filter(Boolean) as any[];
        return (
          <div className="flex w-full items-center justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
            <Tooltip label="Open detail"><Button size="sm" variant="text" aria-label="Open detail" icon={<Eye size={16} />} onClick={() => navigate(`/retailers/${r.id}`)} /></Tooltip>
            {canEdit && (<Tooltip label="Edit"><Button size="sm" variant="text" aria-label="Edit" icon={<Pencil size={16} />} onClick={() => { setEditing(r); setFormOpen(true); }} /></Tooltip>)}
            <ActionMenu items={items} label="More actions" />
          </div>
        );
      },
    },
  ];

  return (
    <div>
      {/* Header row — page title + primary actions, above the filters. */}
      <div className="mb-3 flex flex-wrap items-center gap-3">
        {!embedded && <Typography.Title level={4} style={{ margin: 0 }}>Retailers</Typography.Title>}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {canCreate && (
            <Button variant="primary" size="sm" icon={<Plus size={16} />} onClick={() => { setEditing(null); setFormOpen(true); }}>Add retailer</Button>
          )}
          {/*
            * One menu instead of four buttons — the same consolidation the product register uses.
            *
            * "Import CSV", "Export", "CSV for re-import" and "Template" all competed at one level,
            * and three of them named a FILE rather than an action: nothing distinguished "Export"
            * from "CSV for re-import" without knowing that one is a formatted register and the other
            * round-trips through the importer. Worse, "CSV for re-import" was a borderless text
            * button with no icon sitting between three bordered ones, so it read as a stray link.
            * Grouped by intent, each line says what it is FOR and the format is a detail.
            */}
          {(canExport || canImport) && (
            <Dropdown
              align="end"
              panel={
                <>
                  {canExport && <MenuLabel>Download</MenuLabel>}
                  {canExport && (
                    <MenuItem icon={<Download size={16} />} onClick={() => setExportOpen(true)}>
                      Register — PDF or Excel
                    </MenuItem>
                  )}
                  {canExport && (
                    <MenuItem icon={<FileSpreadsheet size={16} />} disabled={exporting} onClick={exportCsv}>
                      Retailer data — CSV
                    </MenuItem>
                  )}
                  {canExport && canImport && <MenuDivider />}
                  {canImport && <MenuLabel>Bulk import</MenuLabel>}
                  {canImport && (
                    <MenuItem icon={<Upload size={16} />} onClick={() => setImportOpen(true)}>
                      Upload a filled file — CSV or Excel…
                    </MenuItem>
                  )}
                  {canImport && (
                    <MenuItem
                      icon={<FileDown size={16} />}
                      onClick={() => downloadCsv('/imports/templates/retailers.csv', 'retailers-template.csv')
                        .catch(() => message.error('Template unavailable'))}
                    >
                      Blank template — CSV
                    </MenuItem>
                  )}
                </>
              }
            >
              {/* Two-way hub, so a lone download arrow would announce only half of it. */}
              <Button size="sm" icon={<ArrowUpDown size={16} />}>Import / export</Button>
            </Dropdown>
          )}
        </div>
      </div>
      {/* Headline numbers, computed server-side over the whole scoped register — never off the rows
          on screen, which would change the "total" every time you paged or filtered. */}
      <div className="mb-3 grid grid-cols-2 gap-3 lg:grid-cols-5">
        <StatCard
          label="Retailers" tone="brand" icon={<Store className="size-4" />}
          value={(kpis.total ?? 0).toLocaleString('en-IN')}
          // Every retailer is accounted for, so the parts visibly sum to the headline.
          hint={[
            `${(kpis.active ?? 0).toLocaleString('en-IN')} active`,
            (kpis.onboarding ?? 0) ? `${(kpis.onboarding ?? 0).toLocaleString('en-IN')} onboarding` : '',
            (kpis.blocked ?? 0) ? `${(kpis.blocked ?? 0).toLocaleString('en-IN')} suspended/closed` : '',
          ].filter(Boolean).join(' · ')}
          active={noFilters}
          onClick={clearFilters}
        />
        <StatCard
          label="KYC pending" tone="gold" icon={<ShieldCheck className="size-4" />}
          value={(kpis.kycPending ?? 0).toLocaleString('en-IN')}
          hint={kpis.kycAwaiting ? `${kpis.kycAwaiting.toLocaleString('en-IN')} waiting on a verifier` : 'None submitted yet'}
          active={kycPendingOnly}
          onClick={() => { const on = !kycPendingOnly; setKycPendingOnly(on); setStatusF([]); if (on) { setHasOutstanding(false); setAgreementDue(false); setDocsPending(undefined); } setPage(1); }}
        />
        <StatCard
          label="Outstanding" tone={kpis.outstanding ? 'danger' : 'success'} icon={<IndianRupee className="size-4" />}
          value={inr2(kpis.outstanding ?? 0)}
          hint={`${(kpis.owing ?? 0).toLocaleString('en-IN')} dealer${(kpis.owing ?? 0) === 1 ? '' : 's'} owing`}
          active={hasOutstanding}
          onClick={() => { const on = !hasOutstanding; setHasOutstanding(on); if (on) { setStatusF([]); setAgreementDue(false); setKycPendingOnly(false); setDocsPending(undefined); } setPage(1); }}
        />
        <StatCard
          // "Due" covers never-signed as well as expiring: a dealer trading without any agreement is
          // the bigger gap, and counting only renewals made the tile read 0 while 312 had none.
          label="Agreements due" tone={kpis.agreementDue ? 'warning' : 'neutral'} icon={<FileWarning className="size-4" />}
          value={(kpis.agreementDue ?? 0).toLocaleString('en-IN')}
          hint={[
            (kpis.agreementMissing ?? 0) ? `${(kpis.agreementMissing ?? 0).toLocaleString('en-IN')} never signed` : '',
            (kpis.agreementNoCopy ?? 0) ? `${(kpis.agreementNoCopy ?? 0).toLocaleString('en-IN')} copy missing` : '',
            (kpis.agreementExpired ?? 0) ? `${kpis.agreementExpired} expired` : '',
            (kpis.agreementExpiring ?? 0) ? `${kpis.agreementExpiring} expiring` : '',
          ].filter(Boolean).join(' · ') || 'All current'}
          active={agreementDue}
          onClick={() => { const on = !agreementDue; setAgreementDue(on); if (on) { setStatusF([]); setHasOutstanding(false); setKycPendingOnly(false); setDocsPending(undefined); } setPage(1); }}
        />
        <StatCard
          // Distinct from "KYC pending": that counts dealers nobody has VERIFIED, this counts the ones
          // whose file physically cannot be verified yet because a document is missing. The second is
          // the collection worklist — you chase paperwork, not a decision.
          label="KYC details missing" tone={kpis.docsPending ? 'danger' : 'success'} icon={<FileStack className="size-4" />}
          value={(kpis.docsPending ?? 0).toLocaleString('en-IN')}
          hint={kpis.docsPending
            ? `${(kpis.docsMissingTotal ?? 0).toLocaleString('en-IN')} item${(kpis.docsMissingTotal ?? 0) === 1 ? '' : 's'} to collect`
            : 'Every file is complete'}
          active={docsPending === 'pending'}
          onClick={() => {
            const on = docsPending !== 'pending';
            setDocsPending(on ? 'pending' : undefined);
            if (on) { setStatusF([]); setHasOutstanding(false); setKycPendingOnly(false); setAgreementDue(false); }
            setPage(1);
          }}
        />
      </div>
      <Card>
        <Space wrap size={[8, 8]} style={{ marginBottom: 12, width: '100%' }}>
          <Input prefix={<Search size={16} />} placeholder="Search company, contact, mobile, code…" aria-label="Search company, contact, mobile, code" allowClear
            style={{ width: 320 }} value={searchInput} onChange={(e) => { setSearchInput(e.target.value); setPage(1); }} />
          <Select mode="multiple" allowClear showSearch placeholder="State" style={{ minWidth: 150 }} maxTagCount={1}
            optionRender={renderFacetOption}
            value={stateF} onChange={(v) => { setStateF(v); setPage(1); }} options={stateOpts} />
          <Select mode="multiple" allowClear showSearch placeholder="City" style={{ minWidth: 140 }} maxTagCount={1}
            optionRender={renderFacetOption}
            value={cityF} onChange={(v) => { setCityF(v); setPage(1); }} options={opt(facets?.city)} />
          {/* One option per missing item, so "who still owes a PAN?" is one pick — and the same list
              exports, because the export carries these params. Counts are the tile's, over the whole
              register, the same way the other facets count. Each option wears a dot in the colour of
              what it finds (red = blocks KYC, like the tags in the rows), and the closed control takes
              that tint, so a set filter says what it is set to. */}
          <Select
            allowClear
            placeholder="KYC details"
            style={{ minWidth: 230, ...filterTint(docsPending ? KYC_FILTER_HUE[docsPending] : undefined) }}
            value={docsPending}
            onChange={(v: any) => { setDocsPending(v || undefined); setPage(1); }}
            optionRender={renderFacetOption}
            options={kycDetailOptions}
          />
          <Select mode="multiple" allowClear placeholder="Status" style={{ minWidth: 150 }} maxTagCount={1}
            optionRender={renderFacetOption}
            value={statusF} onChange={(v) => { setStatusF(v); setPage(1); }}
            options={opt(facets?.status, (r) => retailerStatusLabel(String(r.value)))} />
          <Tooltip label="Only retailers with an outstanding balance">
            <Button variant={hasOutstanding ? 'primary' : 'default'} onClick={() => { setHasOutstanding((v) => !v); setPage(1); }}>Outstanding</Button>
          </Tooltip>
          <Select mode="multiple" allowClear placeholder="Credit days" style={{ minWidth: 130 }} maxTagCount={1}
            optionRender={renderFacetOption}
            value={creditF} onChange={(v) => { setCreditF(v); setPage(1); }} options={creditOpts} />
          {/* A rep only ever sees their own retailers, so a "filter by rep" they can't populate is hidden. */}
          {canViewUsers && (
            <Select mode="multiple" allowClear showSearch optionFilterProp="label" placeholder="Sales person" style={{ minWidth: 180 }} maxTagCount={1}
              value={salesF} onChange={(v) => { setSalesF(v); setPage(1); }}
              options={(salesUsers?.data ?? []).map((u: any) => ({ value: String(u.id), label: u.fullName }))} />
          )}
          <Select mode="multiple" allowClear showSearch optionFilterProp="label" placeholder="Territory" style={{ minWidth: 180 }} maxTagCount={1}
            value={territoryF} onChange={(v) => { setTerritoryF(v); setPage(1); }}
            options={(territories ?? [])
              .map((t: any) => ({ value: String(t.id), ...territoryOption(t) }))
              // Sorted by the plain text, not the label — the label is a node (code chip + name) now.
              .sort((a, b) => a.searchLabel.localeCompare(b.searchLabel))} />
          {activeFilters > 0 && (
            <FilterResetButton size="md" count={activeFilters} onClick={clearFilters} />
          )}
        </Space>

        <DataTable
          storageKey="retailers-list"
          rowKey="id"
          dataSource={data?.data ?? []}
          loading={isLoading || isFetching}
          columns={columns}
          sortState={sortState}
          onSortChange={onSortChange}
          onRow={(r) => {
            const ag = agreementStatus(r.agreementExpiry);
            return {
              onClick: () => navigate(`/retailers/${r.id}`),
              style: {
                cursor: 'pointer',
                // Tint the whole row red when the dealer agreement is expiring soon or expired.
                background: ag.alert ? '#fff1f0' : undefined,
              },
            };
          }}
          scroll={{ x: 1300 }}
          pagination={{
            current: page,
            pageSize,
            total: data?.meta?.total ?? 0,
            showSizeChanger: true,
            showTotal: (t) => `${t.toLocaleString('en-IN')} retailers`,
            onChange: (p, ps) => { setPage(p); setPageSize(ps); },
          }}
        />
      </Card>

      <RetailerFormModal open={formOpen} onClose={() => { setFormOpen(false); setEditing(null); }} record={editing} />
      {/* No period picker: the dealer register is a "who is on the books right now" snapshot, not a
          transaction list — a date range would filter nothing. Page filters carry through. */}
      <ExportDialog
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        title="Export dealers"
        description="The dealer register with credit limits and outstanding, highest dues first. Your current filters apply."
        baseUrl="/retailers/export"
        baseParams={params}
        hidePeriod
      />
      <BulkImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onDone={() => qc.invalidateQueries({ queryKey: ['retailers'] })}
        kind="retailers"
        entityPlural="retailers"
        allowUpsert
        columns="code, companyName, contactPerson, mobile, email, gstNumber, panNumber, addressLine1, city, stateCode, pincode, latitude, longitude, territoryCode, creditLimit, creditPeriodDays, openingBalance"
        note={<>Required: <b>companyName, mobile, territoryCode</b>. Leave <b>code</b> blank to auto-assign (R-######), or provide one to keep it. </>}
      />
      <Modal
        open={!!confirmStatus}
        onOpenChange={(o) => { if (!o) setConfirmStatus(undefined); }}
        title={confirmStatus?.status === 'ACTIVE' ? 'Deactivate this retailer?' : 'Activate this retailer?'}
        footer={
          <>
            <Button variant="default" onClick={() => setConfirmStatus(undefined)}>Cancel</Button>
            <Button
              variant={confirmStatus?.status === 'ACTIVE' ? 'danger' : 'primary'}
              loading={statusMut.isPending}
              onClick={() => {
                statusMut.mutate({ id: String(confirmStatus.id), active: confirmStatus.status !== 'ACTIVE' });
                setConfirmStatus(undefined);
              }}
            >
              {confirmStatus?.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}
            </Button>
          </>
        }
      >
        <p className="type-body text-muted">
          <b className="text-content">{confirmStatus?.companyName}</b>{' '}
          {confirmStatus?.status === 'ACTIVE'
            ? 'will be marked SUSPENDED — they cannot place orders until reactivated. Outstanding dues can still be collected.'
            : 'will be marked ACTIVE and can trade again.'}
        </p>
      </Modal>
      {canMerge && <MergeCustomerModal open={!!mergingDup} onClose={() => setMergingDup(null)} customerType="RETAILER" duplicate={mergingDup ?? {}} />}
    </div>
  );
}
